// Ontology constants shared by client, server, and the corpus importer.
// Source of truth: docs/practice-topic-ontology.md (26 Jul 2026 revision).
// Topic copy is the approved mockup copy from mockups/fable/assets/data.js;
// it is user-visible, so it says "practice", never "card", and holds the
// repo style rules.

export const EVIDENCE_CHIPS = [
  "Established",
  "Emerging",
  "Expert viewpoint",
  "Contested",
] as const;
export type EvidenceChip = (typeof EVIDENCE_CHIPS)[number];

// Ontology section 6: the internal research vocabulary. "Not yet reviewed"
// never ships; there is no user chip for it.
export const EDITORIAL_STATUSES = [
  "Well supported",
  "Promising but early",
  "Mixed evidence",
  "Actively disputed",
  "Insufficient evidence",
  "Expert viewpoint",
  "Not yet reviewed",
] as const;
export type EditorialStatus = (typeof EDITORIAL_STATUSES)[number];

export const STATUS_TO_CHIP: Record<EditorialStatus, EvidenceChip | null> = {
  "Well supported": "Established",
  "Promising but early": "Emerging",
  "Insufficient evidence": "Emerging",
  "Expert viewpoint": "Expert viewpoint",
  "Mixed evidence": "Contested",
  "Actively disputed": "Contested",
  "Not yet reviewed": null,
};

// Ontology section 1: the 21 primary health domains.
export const DOMAINS = [
  "Nutrition and food",
  "Metabolic health",
  "Movement and performance",
  "Sleep and circadian health",
  "Mind, behavior, and relationships",
  "Longevity and aging",
  "Environment and exposures",
  "Gut and microbiome",
  "Hormones, reproductive, and sexual health",
  "Brain and cognitive health",
  "Cardiovascular health",
  "Immune health and inflammation",
  "Musculoskeletal health and pain",
  "Prevention and clinical care",
  "Supplements, self-administered therapies, and health technology",
  "Clinic-administered and regenerative therapies",
  "Skin and appearance",
  "Meaning, spirituality, and religion",
  "Mortality and the far edge",
  "AI and digital health tools",
  "Navigating and paying for care",
] as const;

// Ontology section 13: the topic layer. Every practice routes to exactly
// one topic; only `filterable` topics are user-facing filters (the rows
// the filter UI shows), and `reason` is the stated reason behind the
// per-row help control (disclosure, not removal). Everyday kinds keep
// their entries for routing, chips, and decline scopes, but always flow.
export interface TopicDef {
  id: string;
  label: string;
  short: string;
  domain: string;
  defaultOn: boolean;
  // Filterable topics are the ones a user might genuinely want to hold
  // back, and they are the only rows the filter UI shows (28 Jul 2026).
  // Everyday kinds route content internally but always flow.
  filterable: boolean;
  reason: string;
}

export const TOPICS: TopicDef[] = [
  {
    id: "sleep",
    label: "Sleep and light",
    short: "Sleep",
    domain: "sleep",
    defaultOn: true,
    filterable: false,
    reason:
      "Sleep duration, quality and timing, plus circadian rhythm, light exposure, the sleep environment, naps, and shift work. On by default: it is the foundation most other practices sit on.",
  },
  {
    id: "movement",
    label: "Movement and training",
    short: "Movement",
    domain: "movement",
    defaultOn: true,
    filterable: false,
    reason:
      "Strength, muscle, aerobic fitness, zone 2 and high-intensity work, mobility, balance, walking, and recovery. On by default, and it covers everyday movement as well as structured training.",
  },
  {
    id: "food",
    label: "Food quality",
    short: "Food",
    domain: "nutrition",
    defaultOn: true,
    filterable: false,
    reason:
      "Dietary patterns, food quality and processing, protein and fibre, micronutrients, hydration, and food preparation. On by default. Whole-diet changes live in their own topic below.",
  },
  {
    id: "exposure",
    label: "Environmental exposures",
    short: "Exposures",
    domain: "environment",
    defaultOn: true,
    filterable: false,
    reason:
      "Plastics, endocrine disruptors, air and water quality, mould, heavy metals, pesticides, household and personal care products. On by default, with dose and absolute risk stated rather than implied.",
  },
  {
    id: "behaviour",
    label: "Habits and behaviour",
    short: "Habits",
    domain: "mind",
    defaultOn: true,
    filterable: false,
    reason:
      "Habit formation, motivation, stress, mood, meditation and breathwork, alcohol and quitting, plus relationships and purpose. On by default. Mental health as a clinical topic is separate, below.",
  },
  {
    id: "timing",
    label: "Meal timing and short fasts",
    short: "Meal timing",
    domain: "nutrition",
    defaultOn: true,
    filterable: true,
    reason:
      "Includes eating windows up to 16 hours. Worth filtering if you take glucose-lowering medication, are pregnant, or have a history of disordered eating.",
  },
  {
    id: "longfast",
    label: "Extended fasting, 24 hours and over",
    short: "Fasting",
    domain: "nutrition",
    defaultOn: false,
    filterable: true,
    reason:
      "Fasts of a day and over genuinely interact with medication and pregnancy. What ships is the full protocol as the expert teaches it, with their caveats and ours.",
  },
  {
    id: "restrictive",
    label: "Restrictive and elimination diets",
    short: "Diets",
    domain: "nutrition",
    defaultOn: false,
    filterable: true,
    reason:
      "Carnivore, raw vegan, and elimination protocols: a whole-diet change is a bigger commitment than a daily practice, and that is the only reason this filter exists.",
  },
  {
    id: "supplements",
    label: "Supplements and compounds",
    short: "Supplements",
    domain: "selfadmin",
    defaultOn: false,
    filterable: true,
    reason:
      "Where commercial conflicts concentrate. Every supplement practice names its conflicts, including when the expert co-founded the company selling it.",
  },
  {
    id: "detox",
    label: "Detox, binders and chelation",
    short: "Detox",
    domain: "selfadmin",
    defaultOn: false,
    filterable: true,
    reason:
      "No validated protocol exists, and several roster members sell the products. What ships is labelled Contested with the seller relationship stated.",
  },
  {
    id: "vaccines",
    label: "Vaccines and immunisation",
    short: "Vaccines",
    domain: "prevention",
    defaultOn: false,
    filterable: true,
    reason:
      "Contested territory, and rarely what someone came here for. What ships carries the same evidence labels as everything else.",
  },
  {
    id: "hormones",
    label: "Hormones and prescription-adjacent",
    short: "Hormones",
    domain: "hormones",
    defaultOn: false,
    filterable: true,
    reason:
      "These decisions belong with a prescriber, so this content appears as reading rather than as a daily checkbox.",
  },
  {
    id: "mental",
    label: "Mental health",
    short: "Mind",
    domain: "mind",
    defaultOn: false,
    filterable: true,
    reason:
      "Crisis content routes to help rather than to a checklist; that is a safety behaviour, not a content filter.",
  },
  // The three wider-universe switches the ontology reserved (section 13),
  // added 28 Jul 2026 when the corpus surfaced 96 practices needing them.
  // Sixteen switches was named the ceiling; this is it.
  {
    id: "skin",
    label: "Skin",
    short: "Skin",
    domain: "prevention",
    defaultOn: true,
    filterable: false,
    reason:
      "Sun protection, retinoids and skin-barrier care, acne and ageing-skin routines. On by default: mostly low-risk daily habits, with prescription-strength treatments labeled as such.",
  },
  {
    id: "sex",
    label: "Sex and intimacy",
    short: "Intimacy",
    domain: "hormones",
    defaultOn: false,
    filterable: true,
    reason:
      "Intimate content is a choice, not an assumption. Practices about desire, sexual function and intimacy arrive labeled and sourced like anything else.",
  },
  {
    id: "screening",
    label: "Screening and tests",
    short: "Screening",
    domain: "prevention",
    defaultOn: false,
    filterable: true,
    reason:
      "Tests, scans and checkups: who benefits at what age, how often, and the overdiagnosis debates. Whether to have a specific test is a decision for you and your clinician.",
  },
  {
    id: "mortality",
    label: "Meaning and mortality",
    short: "Meaning",
    domain: "mind",
    defaultOn: false,
    filterable: true,
    reason:
      "Deeply personal territory, from palliative care and end-of-life planning through religion and cryonics. It arrives only from the experts you chose to follow.",
  },
];

export const TOPIC_BY_LABEL = new Map(TOPICS.map((t) => [t.label, t]));

// Ontology section 9: the attribution ladder.
export const ATTRIBUTIONS = ["direct", "paraphrased", "summary", "inferred"] as const;
export type Attribution = (typeof ATTRIBUTIONS)[number];

// Research plan: one primary expert_type per personal expert.
export const EXPERT_TYPES = [
  "clinician",
  "researcher",
  "communicator",
  "journalist",
  "coach",
  "influencer",
  "founder",
  "self-experimenter",
] as const;
export type ExpertType = (typeof EXPERT_TYPES)[number];

// Deferral vocabulary: real durations, and "no thanks" scopes.
export const SNOOZE_DURATIONS = ["week", "month", "quarter", "season", "year"] as const;
export type SnoozeDuration = (typeof SNOOZE_DURATIONS)[number];

export const DECLINE_SCOPES = ["card", "expert", "topic"] as const;
export type DeclineScope = (typeof DECLINE_SCOPES)[number];

export const CARD_VERSIONS = ["minimal", "standard", "full"] as const;
export type CardVersionName = (typeof CARD_VERSIONS)[number];

// The cadence picker for a practice you create yourself. Structured, not
// free text: the Today check-list is frequency-aware through weeklyTarget
// (src/shared/cadence.ts), and a cadence it cannot parse silently becomes
// daily. Every `cadence` string here parses to the target you would expect.
export const CUSTOM_CADENCES = [
  { id: "daily", label: "Daily", cadence: "Daily" },
  { id: "2x-week", label: "Twice a week", cadence: "2x per week" },
  { id: "3x-week", label: "3 times a week", cadence: "3x per week" },
  { id: "4x-week", label: "4 times a week", cadence: "4x per week" },
  { id: "5x-week", label: "5 times a week", cadence: "5x per week" },
  { id: "weekly", label: "Weekly", cadence: "Weekly" },
  { id: "once", label: "Once", cadence: "Once" },
  { id: "as-needed", label: "As needed", cadence: "As needed" },
] as const;
export type CustomCadenceId = (typeof CUSTOM_CADENCES)[number]["id"];
